import React, { useRef } from "react";
import { AnimateOnScroll } from "./AnimateOnScroll";
import { motion } from "framer-motion";

const featuresData = [
  {
    title: "One-Time Use Codes",
    description:
      "Generate secure, single-use codes for limited access or promotions — ideal for event passes or time-sensitive offers.",
    icon: (
      <svg
        className="w-6 h-6 text-[#1a1f6c]"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
        />
      </svg>
    ),
  },
  {
    title: "Bounce Backs",
    description:
      "Encourage repeat visits by automatically sending return offers after a purchase or session.",
    icon: (
      <svg
        className="w-6 h-6 text-[#1a1f6c]"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
    ),
  },
  {
    title: "Happy Hours",
    description:
      "Apply automatic time-based pricing for peak or off-peak hours, ideal for parking or retail promos.",
    icon: (
      <svg
        className="w-6 h-6 text-[#1a1f6c]"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    title: "Discounts",
    description:
      "Offer fixed or percentage-based discounts through the kiosk interface for eligible users or campaigns.",
    icon: (
      <svg
        className="w-6 h-6 text-[#1a1f6c]"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 7h.01M17 17h.01M7 17h.01M17 7h.01M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
        />
      </svg>
    ),
  },
];

export const CarwashFeatures = () => {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === "left" ? -440 : 440,
        behavior: "smooth",
      });
    }
  };

  return (
    <section className="relative py-24 px-6 lg:px-8 overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="/images/carwash-sec/hero-bg.webp"
          alt=""
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#2b2e34]/90" />
      </div>

      <div className="relative z-10 max-w-screen-xl mx-auto">
        <AnimateOnScroll>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white text-center mb-16 leading-tight">
            Smart Features That <br />
            Power Every Kinitas Kiosk
          </h2>
        </AnimateOnScroll>

        {/* Cards scroll container */}
        <div
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth hide-scrollbar"
        >
          {featuresData.map((item, index) => (
            <motion.div
              key={item.title}
              className="snap-start flex-shrink-0 w-[85vw] sm:w-[350px] bg-white rounded-[20px] p-7 flex flex-col"
              style={{ minHeight: "320px" }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
            >
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-5">
                {item.icon}
              </div>
              <h3
                className="font-bold text-[#111827]"
                style={{
                  maxWidth: "214px",
                  fontSize: "20px",
                  lineHeight: "29px",
                }}
              >
                {item.title}
              </h3>
              <div
                style={{
                  marginTop: "218px",
                  marginTop: "clamp(16px, 5vw, 218px)",
                }}
              >
                <p
                  className="text-[15px] text-[#555] leading-relaxed"
                  style={{ maxWidth: "346px" }}
                >
                  {item.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Nav buttons */}
        <div className="flex justify-end gap-3 mt-8">
          <button
            onClick={() => scroll("left")}
            className="w-10 h-10 rounded-lg bg-white/20 text-white flex items-center justify-center hover:bg-white/30 transition-colors duration-300"
            aria-label="Scroll left"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <button
            onClick={() => scroll("right")}
            className="w-10 h-10 rounded-lg bg-white/20 text-white flex items-center justify-center hover:bg-white/30 transition-colors duration-300"
            aria-label="Scroll right"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};
